import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Users, Network, Briefcase,
  CalendarClock, GraduationCap, UserMinus, ClipboardList,
  ShieldCheck, Settings, LogOut, FileUp, Factory, Sparkles,
  BarChart3, Wand2, Mail, LayoutGrid, Crown, Award,
  TrendingDown, Clock, Activity, MessageSquare, FileText, UserSearch, Rocket, Bell, Trash2,
  Truck, Wallet, Layers, Calculator, ClipboardCheck, Eraser, Target, Wrench,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";

const Link = ({ to, icon: Icon, children, end = false, moduleKey, can }) => {
  // Filtra por permissão granular quando informado
  if (moduleKey && !can(moduleKey, "view")) return null;
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={`nav-${to.replace(/^\//, "").replace(/\//g, "-") || "dashboard"}`}
      className={({ isActive }) =>
        `rh-sidebar-link flex items-center gap-3 px-4 py-2.5 text-[13.5px] font-medium ${
          isActive ? "" : "text-white/75 hover:text-white"
        }`
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0 opacity-85" />
      <span className="truncate">{children}</span>
    </NavLink>
  );
};

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const nav = useNavigate();

  return (
    <aside
      data-testid="sidebar"
      className="flex h-full w-[260px] flex-col bg-[#1A231F] text-white shadow-xl"
    >
      <div className="flex items-center gap-3 px-5 pt-6 pb-5 border-b border-white/5">
        <div className="grid h-10 w-10 place-items-center rounded-md bg-[#234935] ring-1 ring-[#C5A059]/30">
          <Factory className="h-5 w-5 text-[#C5A059]" />
        </div>
        <div className="leading-tight">
          <div className="font-display text-[15px] font-semibold tracking-wide">TheoKratos <span className="text-[#C5A059]">RH</span></div>
          <div className="text-[10.5px] uppercase tracking-[0.2em] text-white/50">Plataforma Inteligente</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3" onClick={onNavigate}>
        <div className="px-5 pb-2 text-[10px] uppercase tracking-[0.22em] text-white/40">Visão Geral</div>
        <Link to="/dashboard" icon={LayoutDashboard} end can={can} moduleKey="dashboard">Dashboard</Link>
        <Link to="/diretoria" icon={Crown} can={can} moduleKey="dashboard_diretoria">Painel da Diretoria</Link>
        <Link to="/dashboard-grupo" icon={BarChart3} can={can} moduleKey="dashboard_grupo">Dashboard do Grupo</Link>

        <div className="px-5 pt-4 pb-2 text-[10px] uppercase tracking-[0.22em] text-white/40">Organização</div>
        <Link to="/empresas" icon={Building2} can={can} moduleKey="companies">Empresas</Link>
        <Link to="/setores-e-quadro" icon={Network} can={can} moduleKey="sectors">Setores e Quadro</Link>

        <div className="px-5 pt-4 pb-2 text-[10px] uppercase tracking-[0.22em] text-white/40">Pessoas</div>
        <Link to="/colaboradores" icon={Users} can={can} moduleKey="employees">Colaboradores</Link>
        <Link to="/afastamentos" icon={Activity} can={can} moduleKey="employees">Afastamentos</Link>
        <Link to="/cartas-promocao" icon={FileText} can={can} moduleKey="promotion_letters">Cartas de Promoção</Link>
        <Link to="/escalas" icon={CalendarClock} can={can} moduleKey="schedules">Escalas &amp; Jornadas</Link>
        <Link to="/importacao" icon={FileUp} can={can} moduleKey="employees_import">Importação Excel</Link>
        <Link to="/importacao-especifica" icon={Eraser} can={can} moduleKey="employees_specific_import">Importação Específica</Link>

        <div className="px-5 pt-4 pb-2 text-[10px] uppercase tracking-[0.22em] text-white/40">Inteligência</div>
        <Link to="/relatorios-ia" icon={Sparkles} can={can} moduleKey="ai_reports">Relatórios com IA</Link>
        <Link to="/relatorios-builder" icon={Wand2} can={can} moduleKey="report_builder">Builder de Relatórios</Link>
        <Link to="/relatorios/designer" icon={Layers} can={can} moduleKey="report_designer">Designer de Relatórios</Link>
        <Link to="/simulador-clt-pj" icon={Calculator} can={can} moduleKey="clt_pj_simulator">Simulador CLT × PJ</Link>
        <Link to="/formularios" icon={ClipboardCheck} can={can} moduleKey="smart_forms">Formulários Inteligentes</Link>

        <div className="px-5 pt-4 pb-2 text-[10px] uppercase tracking-[0.22em] text-white/40">Performance & Analytics</div>
        <Link to="/performance" icon={Activity} can={can} moduleKey="performance">Dashboard Executivo RH</Link>
        <Link to="/performance/turnover" icon={TrendingDown} can={can} moduleKey="turnover">Turnover</Link>
        <Link to="/performance/absenteismo" icon={Clock} can={can} moduleKey="absenteismo">Absenteísmo</Link>
        <Link to="/operacional/horas-extras" icon={TrendingDown} can={can} moduleKey="horas_extras">Horas Extras</Link>
        <Link to="/operacional/banco-horas" icon={Wallet} can={can} moduleKey="banco_horas">Banco de Horas</Link>
        <Link to="/cadastros/equipamentos" icon={Truck} can={can} moduleKey="equipments">Equipamentos</Link>
        <Link to="/performance/setores" icon={BarChart3} can={can} moduleKey="sector_indicators">Indicadores por Setor</Link>
        <Link to="/performance/individuos" icon={UserSearch} can={can} moduleKey="performance_individual">Relatórios Individuais</Link>
        <Link to="/performance/onboarding" icon={Rocket} can={can} moduleKey="onboarding">Trilhas de Onboarding</Link>
        <Link to="/pesquisas" icon={MessageSquare} can={can} moduleKey="surveys">Pesquisas & Engajamento</Link>
        <Link to="/notificacoes" icon={Bell} can={can} moduleKey="notifications">Notificações & E-mails</Link>

        <div className="px-5 pt-4 pb-2 text-[10px] uppercase tracking-[0.22em] text-white/40">Gestão</div>
        <Link to="/gestao" icon={Settings} can={can} moduleKey="hub">Gestão · Hub</Link>
        <Link to="/cargos-salarios" icon={Briefcase} can={can} moduleKey="job_plans">Cargos e Salários</Link>
        <Link to="/recrutamento" icon={ClipboardList} can={can} moduleKey="recruitment">Recrutamento</Link>
        <Link to="/treinamentos" icon={GraduationCap} can={can} moduleKey="trainings">Treinamentos</Link>
        <Link to="/avaliacoes" icon={Award} can={can} moduleKey="appraisals">Avaliação &amp; PDI</Link>
        <Link to="/desligamentos" icon={UserMinus} can={can} moduleKey="terminations">Desligamentos</Link>

        {user && (user.role === "master" || user.role === "rh_admin" || user.role === "diretoria") && (
          <>
            <div className="px-5 pt-4 pb-2 text-[10px] uppercase tracking-[0.22em] text-white/40">Administração</div>
            <Link to="/auditoria" icon={ShieldCheck} can={can} moduleKey="audit">Auditoria</Link>
            {(user.role === "master" || user.role === "rh_admin") && (
              <Link to="/admin/situacoes-absenteismo" icon={Clock} can={can} moduleKey="absence_situations">Situações de Absenteísmo</Link>
            )}
            {(user.role === "master" || user.role === "rh_admin") && (
              <Link to="/admin/usuarios" icon={Settings} can={can} moduleKey="admin_users">Usuários &amp; Perfis</Link>
            )}
            {(user.role === "master" || user.role === "rh_admin" || user.role === "diretoria") && (
              <Link to="/admin/permissoes" icon={ShieldCheck} can={can} moduleKey="admin_permissions">Permissões</Link>
            )}
            {(user.role === "master" || user.role === "rh_admin" || user.role === "diretoria") && (
              <Link to="/admin/bloqueios" icon={ShieldCheck} can={can} moduleKey="seguranca_auditoria">Trilha de Bloqueios</Link>
            )}
            {(user.role === "master" || user.role === "rh_admin") && (
              <Link to="/admin/integracoes" icon={Mail} can={can} moduleKey="integrations">Integrações</Link>
            )}
            {(user.role === "master" || user.role === "rh_admin") && (
              <Link to="/admin/emails" icon={Mail} can={can}>Notificações por E-mail</Link>
            )}
            {(user.role === "master" || user.role === "rh_admin") && (
              <Link to="/admin/metas" icon={Target} can={can}>Metas do Grupo</Link>
            )}
            {(user.role === "master" || user.role === "rh_admin") && (
              <Link to="/gestao-em-lote" icon={Wrench} can={can}>Gestão em Lote</Link>
            )}
            {user.role === "master" && (
              <Link to="/admin/limpeza-base" icon={Trash2} can={can} moduleKey="admin_cleanup">Limpeza da Base</Link>
            )}
          </>
        )}
      </nav>

      <div className="border-t border-white/5 p-4">
        <div className="flex items-center gap-3 rounded-md bg-white/[0.04] px-3 py-2.5 mb-3">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-[#C5A059] text-[#1A231F] font-semibold font-display">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[13px] font-medium">{user?.name}</div>
            <div className="truncate text-[10.5px] uppercase tracking-wider text-white/50">{user?.role_label || user?.role}</div>
          </div>
        </div>
        <button
          data-testid="sidebar-logout-btn"
          onClick={async () => { await logout(); nav("/login"); }}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-white/10 px-3 py-2 text-[12.5px] font-medium text-white/80 transition-colors duration-200 hover:bg-white/[0.06] hover:text-white"
        >
          <LogOut className="h-4 w-4" /> Sair
        </button>
      </div>
    </aside>
  );
}
